import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Shield, KeyRound } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUserEmail, setResetUserEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'employee' });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) { toast.error('All fields are required'); return; }
    try {
      await api.post('/users', form);
      toast.success('User created');
      setDialogOpen(false);
      setForm({ email: '', password: '', name: '', role: 'catalog_manager' });
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create user'); }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Delete user "${email}"?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      await api.put(`/users/${id}`, { role: newRole });
      toast.success('Role updated');
      fetchUsers();
    } catch { toast.error('Failed to update role'); }
  };

  const openResetDialog = (id, email) => {
    setResetUserId(id);
    setResetUserEmail(email);
    setResetPassword('');
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await api.post(`/users/${resetUserId}/reset-password`, { new_password: resetPassword });
      toast.success(`Password reset for ${resetUserEmail}`);
      setResetDialogOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
  };

  return (
    <div data-testid="users-page" className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-[#1A1A1A] tracking-tight">Users</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Manage team access</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="add-user-btn">
          <Plus size={16} className="mr-2" />Add User
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner-gold" /></div>
      ) : (
        <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-hidden">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="border-b border-[#E5E0D8]">
                <th className="text-left py-3 px-6 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Name</th>
                <th className="text-left py-3 px-6 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Email</th>
                <th className="text-left py-3 px-6 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Role</th>
                <th className="text-right py-3 px-6 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-[#E5E0D8] hover:bg-[#F5F2EB] transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#F2E8D5] flex items-center justify-center"><Shield size={14} className="text-[#C5A059]" /></div>
                      <span className="text-sm font-medium text-[#1A1A1A]">{u.name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm text-[#5C5C5C]">{u.email}</td>
                  <td className="py-4 px-6">
                    <Select value={u.role} onValueChange={v => handleRoleChange(u.id, v)}>
                      <SelectTrigger className="w-44 rounded-sm border-[#E5E0D8] h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="super_admin">Master Admin</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => openResetDialog(u.id, u.email)} className="text-[#5C5C5C] hover:text-[#C5A059] p-1" data-testid={`reset-password-${u.id}`} title="Reset Password"><KeyRound size={14} /></button>
                      <button onClick={() => handleDelete(u.id, u.email)} className="text-[#5C5C5C] hover:text-[#9E2A2B] p-1" data-testid={`delete-user-${u.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white rounded-sm border-[#E5E0D8]">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">Add User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="rounded-sm border-[#E5E0D8]" data-testid="user-name-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="rounded-sm border-[#E5E0D8]" data-testid="user-email-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Password</Label>
              <PasswordInput value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} className="rounded-sm border-[#E5E0D8]" data-testid="user-password-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="rounded-sm border-[#E5E0D8]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="super_admin">Super Admin</SelectItem><SelectItem value="catalog_manager">Catalog Manager</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm border-[#E5E0D8]">Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="create-user-btn">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="bg-white rounded-sm border-[#E5E0D8]">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2"><KeyRound size={18} className="text-[#C5A059]" />Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-[#5C5C5C]">Reset password for <strong className="text-[#1A1A1A]">{resetUserEmail}</strong></p>
            <div>
              <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">New Password</Label>
              <PasswordInput value={resetPassword} onChange={e => setResetPassword(e.target.value)} className="rounded-sm border-[#E5E0D8]" data-testid="reset-password-input" placeholder="Minimum 6 characters" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)} className="rounded-sm border-[#E5E0D8]">Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="confirm-reset-password-btn">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
